import type { FC } from 'react'

import { useEffect, useMemo, useRef } from 'react'
import { observeElement } from '@/lib/observe'

interface CountUpProps {
    value: number
    decimals?: number
    prefix?: string
    suffix?: string
    duration?: number
    className?: string
}

/**
 * Renders the final number on the server (so it is never shown as zero to
 * crawlers or without JavaScript) and counts up from zero when scrolled into view.
 */
export const CountUp: FC<CountUpProps> = ({
    value,
    decimals = 0,
    prefix = '',
    suffix = '',
    duration = 1800,
    className
}) => {
    const ref = useRef<HTMLSpanElement>(null)

    const format = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }), [decimals])

    const display = `${prefix}${format.format(value)}${suffix}`

    useEffect(() => {
        const node = ref.current

        if (!node) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let raf = 0
        let started = false
        let stop = () => {}

        const paint = (current: number) => {
            node.textContent = `${prefix}${format.format(current)}${suffix}`
        }

        const run = () => {
            const begin = performance.now()

            const tick = (now: number) => {
                const progress = Math.min(1, (now - begin) / duration)
                const eased = 1 - Math.pow(1 - progress, 4)

                paint(value * eased)

                if (progress < 1) raf = window.requestAnimationFrame(tick)
            }

            raf = window.requestAnimationFrame(tick)
        }

        stop = observeElement(node, entry => {
            if (started) return

            if (entry.isIntersecting) {
                started = true
                paint(0)
                run()
                stop()
            }
            else {
                paint(0)
            }
        }, { rootMargin: '0px 0px -8% 0px' })

        return () => {
            stop()
            window.cancelAnimationFrame(raf)
            paint(value)
        }
    }, [value, prefix, suffix, duration, format])

    return (
        <span
            ref={ref}
            className={className}
        >
            {display}
        </span>
    )
}
